import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ChevronLeft, ChevronRight, Plus, Trash2, Settings, CalendarDays,
  ArrowDownLeft, ArrowUpRight, Check, Pencil, X, RefreshCw, AlertTriangle, Sigma,
  CalendarClock, TrendingDown, Clock, Wallet,
} from 'lucide-react';
import { useWS } from '../WSContext';

// Kişisel bütçe — klasik defter. Kurgu backend/python/budget_db.py başında.
//
// İki ilke arayüze de yansıyor:
//   • DURUM TÜRETİLİR: hiçbir yerde "ödendi mi" kutusu yok, yalnız "x tutar ödendi"
//     satırları var. Kalan bunlardan çıkıyor → tutarla çelişemiyor.
//   • KUR YOK: her para birimi kendi hattında toplanıyor, tek bir "toplam" basmıyoruz.
//     Basmak uydurma bir kura bağlı olurdu.

interface Hareket {
  id: number; tarih: string; tutar: number; kisiId: number; notu: string;
  kur: number; kurHam: number; usd: number | null;
}
interface Kalem {
  id: number; ay: string; sablonId: number; kisiId: number; ad: string;
  yon: 'gelir' | 'gider'; tur: string; para: string; tutar: number; vade: string; notu: string;
  odenen: number; kalan: number; durum: 'bekliyor' | 'kismi' | 'odendi'; hareketler: Hareket[];
  // Kredi kartı asgari ödemesi. 0 = asgarisi yok. Hepsi TÜRETİLMİŞ, saklanan
  // tek şey asgari tutarın kendisi.
  asgari: number; asgariOdendi: boolean; asgariKalan: number;
  // Otomatik tahsil (yalnız gelir): maaş bankaya kendiliğinden yatıyor.
  // otoTahsil = tik açık mı, otoTahsilEdildi = vadesi geldi ve elle kayıt
  // olmadığı için TİKTEN sayıldı. İkincisi "ölçülmedi, varsayıldı" demek.
  otoTahsil?: boolean; otoTahsilEdildi?: boolean;
  // Yalnız TRY kalemlerinde dolu (backend FX_PARA):
  usdTutar?: number | null; usdKalan?: number | null; usdOdenen?: number; usdKurYok?: boolean;
  usdAsgariKalan?: number | null;
}
// TRY -> USDC kuru. bid = Binance TR alis, efektif = komisyon dusulmus hali.
interface Fx { para: string; bid: number; kom: number; efektif: number; kaynak: string; ts: number }
interface Kisi { id: number; ad: string; emoji: string; sira: number }
interface Sablon {
  id: number; kisi_id: number; ad: string; yon: string; tur: string; para: string;
  gun: number; tutar: number; otomatik: number; bas_ay: string; son_ay: string;
  oto_tahsil?: number;   // yalnız gelir: banka kendiliğinden yatırıyor
  aktif: number; notu: string; vade?: string;
}
interface BakiyeSatiri {
  ad: string; kurum: string; bakiye: number; tarih: string; kaynak: string;
  kisi: string; emoji: string;
}
interface Ozet {
  para: string; gelirBeklenen: number; gelirGelen: number; gelirKalan: number;
  giderBeklenen: number; giderOdenen: number; giderKalan: number;
  net: number; netGerceklesen: number;
  usdGiderOdenen?: number; usdGiderKalan?: number;
  // Hesaplarda DURAN para. Gelir/giderden ayrı bir şey: o ay ne akacağını
  // değil, şu an ne olduğunu söylüyor. Kırılım hover'da.
  bakiye?: number; bakiyeKirilim?: BakiyeSatiri[];
}
interface Ay {
  success: boolean; error?: string; ay: string; kalemler: Kalem[]; bekleyen: Sablon[];
  ozet: Ozet[]; kisiler: Kisi[]; sablonlar: Sablon[]; fx: Fx;
}

const PARALAR = ['EUR', 'TRY', 'USD'] as const;
const SIMGE: Record<string, string> = { EUR: '€', TRY: '₺', USD: '$' };
const TURLER = ['kart', 'kira', 'kredi', 'abonelik', 'maas', 'diger'] as const;
const TUR_AD: Record<string, string> = {
  kart: 'kredi kartı', kira: 'kira', kredi: 'kredi',
  abonelik: 'abonelik', maas: 'maaş', diger: 'diğer',
};
const AY_AD = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran',
  'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];

const buAy = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const ayKaydir = (ay: string, delta: number) => {
  const y = parseInt(ay.slice(0, 4), 10), m = parseInt(ay.slice(5, 7), 10) - 1 + delta;
  const d = new Date(y, m, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const ayEtiket = (ay: string) => `${AY_AD[parseInt(ay.slice(5, 7), 10) - 1]} ${ay.slice(0, 4)}`;
const para = (n: number, p: string) =>
  `${SIMGE[p] || ''}${(n || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const gunu = (vade: string) => (vade ? parseInt(vade.slice(8, 10), 10) : 0);
// Yaklasik USDC karsiligi. null = kur yok -> sayi uydurmuyoruz.
const usdc = (n: number | null | undefined) =>
  n == null ? '' : '≈$' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const kurZaman = (ts: number) => {
  if (!ts) return '';
  const dk = Math.floor((Date.now() - ts) / 60000);
  if (dk < 1) return 'simdi';
  if (dk < 60) return dk + ' dk once';
  const sa = Math.floor(dk / 60);
  return sa < 24 ? sa + ' sa once' : Math.floor(sa / 24) + ' gun once';
};
// YEREL tarih. toISOString() UTC veriyor: Orta Avrupa'da gece yarısından
// sonra hâlâ "dün" döner, vade karşılaştırması ve tarih kutusu bir gün geri
// kayardı.
const bugun = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// İçinde bulunulan ayın son günü — "ay sonuna kadar" penceresi için.
const ayinSonu = () => {
  const d = new Date();
  const son = new Date(d.getFullYear(), d.getMonth() + 1, 0);
  return `${son.getFullYear()}-${String(son.getMonth() + 1).padStart(2, '0')}-${String(son.getDate()).padStart(2, '0')}`;
};

// ── seçilen kalemlerin toplamı ───────────────────────────────────────────────
// Sunucuya SORULMUYOR: toplanacak her alan (tutar/odenen/kalan/usdOdenen/
// usdKalan) kalemin üstünde zaten duruyor, ikinci kez türetmek olurdu.
//
// İKİ KUR AYNI HÜCREYE GİRMEZ: usdOdenen ödemenin kendi DONMUŞ kurundan
// (gerçekleşmiş maliyet), usdKalan BUGÜNKÜ kurdan (tahmin). Tek bir "≈$"
// rakamında toplamak ölçülmüşle tahmini karıştırmak olurdu — ayrı taşınıyorlar.
//
// Gelir ve gider de ayrı: ikisi birlikte seçilince tek "toplam" anlamsız,
// anlamlı olan NET. Yalnız tek yön seçiliyse net basmıyoruz.
const round2 = (n: number) => Math.round(n * 100) / 100;

// ── ileriye dönük akış (budget_forecast) ─────────────────────────────────────
// SABİT / DEĞİŞKEN ayrımı backend'den geliyor ve arayüzde de ayrı duruyor:
// kart ekstresi her ay elle giriliyor, gelecek ayın karşılığı YOK. Tek rakamda
// toplasaydık gelecek aylar sahte bir rahatlama gösterirdi.
interface FcPara {
  para: string; gelir: number; gelirKalan: number;
  gider: number; giderSabit: number; giderDegisken: number; giderKalan: number;
  net: number; degiskenVar: boolean; projeksiyon: boolean;
  usdGider?: number | null; usdGiderKalan?: number | null;
}
interface FcIsaret { ad: string; para: string; tutar: number }
interface FcAy { ay: string; paralar: FcPara[]; biten: FcIsaret[]; baslayan: FcIsaret[]; satirSayi: number }
interface FcVade {
  kalemId: number; ad: string; tur: string; para: string;
  tutar: number; vade: string; projeksiyon: boolean; usd: number | null;
}
interface FcKredi { ad: string; para: string; tutar: number; sonAy: string; taksit: number; kalanBorc: number }
interface Forecast {
  success: boolean; ay: string; n: number; fx: Fx;
  aylar: FcAy[]; yaklasan: FcVade[]; krediler: FcKredi[];
}
const ayKisa = (ay: string) =>
  `${AY_AD[parseInt(ay.slice(5, 7), 10) - 1].slice(0, 3)} ${ay.slice(2, 4)}`;
// Gün farkı YEREL tarihten: sunucu gün hesaplasaydı gece yarısı sınırında bir
// günlük kayma çıkardı, "bugün ödenecek" yarına kayardı.
const gunFark = (vade: string) => {
  const t0 = new Date(); t0.setHours(0, 0, 0, 0);
  return Math.round((new Date(vade + 'T00:00:00').getTime() - t0.getTime()) / 86400000);
};
interface YonToplam {
  adet: number; tutar: number; gercek: number; kalan: number;
  usdVar: boolean; usdGercek: number; usdKalan: number; kurYok: boolean;
}
interface ParaToplam {
  para: string; adet: number; gider: YonToplam; gelir: YonToplam;
  net: number; netGercek: number;
}
const bosYon = (): YonToplam => ({
  adet: 0, tutar: 0, gercek: 0, kalan: 0,
  usdVar: false, usdGercek: 0, usdKalan: 0, kurYok: false,
});
const secimToplam = (ks: Kalem[]): ParaToplam[] => {
  const by: Record<string, ParaToplam> = {};
  for (const k of ks) {
    const o = (by[k.para] ||= {
      para: k.para, adet: 0, gider: bosYon(), gelir: bosYon(), net: 0, netGercek: 0,
    });
    o.adet++;
    const y = k.yon === 'gelir' ? o.gelir : o.gider;
    y.adet++;
    y.tutar += k.tutar;
    y.gercek += k.odenen;
    // Kalan eksiye düşebilir (fazla ödeme): toplamda başka kalemin borcunu
    // kapatmış gibi görünmesin, 0'da kesiliyor.
    y.kalan += Math.max(k.kalan, 0);
    if (k.usdTutar != null || (k.usdOdenen || 0) > 0 || k.usdKalan != null) {
      y.usdVar = true;
      y.usdGercek += k.usdOdenen || 0;
      y.usdKalan += k.usdKalan || 0;
      if (k.usdKurYok) y.kurYok = true;
    }
  }
  const arr = Object.values(by);
  for (const o of arr) {
    o.net = round2(o.gelir.tutar - o.gider.tutar);
    o.netGercek = round2(o.gelir.gercek - o.gider.gercek);
    for (const y of [o.gider, o.gelir]) {
      y.tutar = round2(y.tutar); y.gercek = round2(y.gercek); y.kalan = round2(y.kalan);
      y.usdGercek = round2(y.usdGercek); y.usdKalan = round2(y.usdKalan);
    }
  }
  return arr.sort((a, b) => b.adet - a.adet || a.para.localeCompare(b.para));
};

interface Props { dark?: boolean }

export default function Budget({ dark = true }: Props) {
  const { sendWs, subscribe, connected } = useWS() as any;

  const [ay, setAy]       = useState(buAy());
  const [d, setD]         = useState<Ay | null>(null);
  const [err, setErr]     = useState('');
  const [ayar, setAyar]   = useState(false);
  const [acik, setAcik]   = useState<Set<number>>(new Set());
  const [yeni, setYeni]   = useState(false);
  const [sec, setSec]     = useState<Set<number>>(new Set());
  const [detay, setDetay] = useState(false);
  const [fc, setFc]       = useState<Forecast | null>(null);
  const [akis, setAkis]   = useState(false);
  const [akisPara, setAkisPara] = useState('');
  const [vadeDetay, setVadeDetay] = useState(false);

  // Ay değişince seçim sıfırlanır: başka ayın kalem id'leri burada bir toplam
  // anlamına gelmez.
  useEffect(() => { setSec(new Set()); setDetay(false); }, [ay]);

  const t = {
    bg:    dark ? 'bg-[#030b1a]' : 'bg-[#f5f8fc]',
    text:  dark ? 'text-blue-50' : 'text-slate-800',
    card:  dark ? 'bg-[#0a1628] border-[#164a9e]' : 'bg-white border-[#d4e0f0]',
    sub:   dark ? 'bg-[#061530] border-[#0a2560]' : 'bg-[#eef3fa] border-[#d4e0f0]',
    muted: dark ? 'text-blue-300/70' : 'text-slate-500',
    inp:   dark ? 'bg-[#03112b] border-[#0a2560] text-blue-50'
                : 'bg-white border-[#d4e0f0] text-slate-800',
  };
  const alan = `px-2 py-1.5 rounded-lg border text-xs ${t.inp}`;

  // bugun ARAYÜZDEN gidiyor: sunucu Finlandiya'da, kullanıcı Orta Avrupa'da —
  // otomatik tahsilin "vadesi geldi mi" kararı yerel güne bakmalı.
  const yukle = useCallback(
    (a: string) => sendWs?.({ type: 'budget_month', payload: { ay: a, bugun: bugun() } }), [sendWs]);

  useEffect(() => {
    const unsub = subscribe?.((m: any) => {
      if (m.type === 'budget_month') {
        if (m.payload?.success === false) setErr(m.payload?.error || 'ay okunamadı');
        else { setErr(''); setD(m.payload); }
      } else if (m.type === 'budget_forecast') {
        if (m.payload?.success !== false) setFc(m.payload);
      } else if (m.type === 'budget_saved' && m.payload?.success === false) {
        setErr(m.payload?.error || 'kaydedilemedi');
      }
    });
    return () => unsub?.();
  }, [subscribe]);

  useEffect(() => { if (connected) yukle(ay); }, [connected, ay, yukle]);
  // Akış HER ZAMAN bu aydan başlar, gezilen aydan değil: "yaklaşan ödeme"
  // bugüne bağlı bir soru, geçmiş aya bakarken de cevabı değişmemeli.
  // d değişince tazelenir (ödeme girildi → kalanlar kaydı).
  useEffect(() => {
    if (connected) sendWs?.({ type: 'budget_forecast', payload: { ay: buAy(), n: 12 } });
  }, [connected, d, sendWs]);

  const kisi = useCallback((id: number) => d?.kisiler.find(k => k.id === id), [d]);
  const gonder = (type: string, payload: any) => { setErr(''); sendWs?.({ type, payload: { ...payload, ay } }); };

  // Vade gününe göre grupla — her kolon gün gün akan kendi listesi.
  // GİDER ve GELİR ayrı kolonlarda: aynı gün içinde karışınca "bu ay ne ödeyeceğim"
  // sorusu, araya giren maaş satırlarıyla bölünüyordu. Gruplama yönden SONRA
  // yapılıyor, yoksa bir kolonda kalem olmayan günler boş başlık basardı.
  const gunGrupla = (ks: Kalem[]) => {
    const g: Record<number, Kalem[]> = {};
    for (const k of ks) (g[gunu(k.vade)] ||= []).push(k);
    return Object.entries(g).map(([gn, l]) => ({ gun: Number(gn), kalemler: l }))
      .sort((a, b) => a.gun - b.gun);
  };
  const kolonlar = useMemo(() => {
    const ks = d?.kalemler || [];
    return {
      gider: gunGrupla(ks.filter(k => k.yon !== 'gelir')),
      gelir: gunGrupla(ks.filter(k => k.yon === 'gelir')),
    };
  }, [d]);
  const kalemSayisi = d?.kalemler.length || 0;

  // Seçim id SETİ olarak tutuluyor ama her yerde KALEMİN KENDİSİ kullanılıyor:
  // silinmiş ya da artık bu ayda olmayan bir id böylece kendiliğinden düşüyor,
  // "3 kalem seçildi" yazıp sıfır toplam basma durumu oluşmuyor.
  const secili = useMemo(
    () => (d?.kalemler || []).filter(k => sec.has(k.id)), [d, sec]);

  // GECİKEN: vadesi geçmiş ve hâlâ kalanı olan. Gelir tarafı da gecikebilir
  // (tahsil edilmemiş maaş) — ayrı tutuluyor, rengi de ayrı: biri borç, biri
  // alacak; aynı kırmızıda göstermek ikisini aynı aciliyete sokardı.
  const geciken = useMemo(() => {
    const t0 = bugun();
    // otoTahsil tikli gelir buraya GİRMEZ: zaten yatıyor, elle kayıt
    // beklenmiyor. (Vadesi gelmemişse kalanı zaten duruyor ama geçmiş de
    // değil; vadesi geldiyse backend tahsil edilmiş sayıyor.)
    const ks = (d?.kalemler || []).filter(
      k => k.kalan > 0.004 && k.vade && k.vade < t0 && !k.otoTahsil);
    return { gider: ks.filter(k => k.yon !== 'gelir'), gelir: ks.filter(k => k.yon === 'gelir') };
  }, [d]);

  // YAKLAŞAN: 7 ve 30 günlük pencere. Backend gün filtresi uygulamıyor, ham
  // vadeleri veriyor — gün hesabı burada, kullanıcının saat diliminde yapılıyor.
  const yaklasan = useMemo(() => {
    if (!fc) return null;
    // Pencere GÜN SAYISIYLA DEĞİL, BİTİŞ TARİHİYLE tanımlı. "30 gün" ayın
    // başında yanıltıcıydı: 4 Kasım'da gelecek ayın kirası da pencereye girip
    // bu ayın yükü çok büyük görünüyordu. Bütçe ayı kapatma işi, pencere de
    // ay sonunda bitmeli.
    const pencere = (bitis: string) => {
      const rs = fc.yaklasan.filter(r => gunFark(r.vade) >= 0 && r.vade <= bitis);
      const paralar: Record<string, number> = {};
      let usd = 0, usdVar = false;
      for (const r of rs) {
        paralar[r.para] = round2((paralar[r.para] || 0) + r.tutar);
        if (r.usd != null) { usd += r.usd; usdVar = true; }
      }
      return { bitis, satirlar: rs, paralar, usd: usdVar ? round2(usd) : null };
    };
    const yedi = new Date(); yedi.setHours(0, 0, 0, 0); yedi.setDate(yedi.getDate() + 7);
    const yediS = `${yedi.getFullYear()}-${String(yedi.getMonth() + 1).padStart(2, '0')}-${String(yedi.getDate()).padStart(2, '0')}`;
    const sonS = ayinSonu();
    return {
      yedi: pencere(yediS),
      // 7 günlük pencere ay sonunu aşabilir (ayın 28'i) — o zaman ay sonu
      // kartı 7 günlükten KÜÇÜK olurdu. Böyle bir durumda ay sonu penceresi
      // yine de doğru: iki kart farklı soruya cevap veriyor.
      aySonu: pencere(sonS), sonGun: sonS,
    };
  }, [fc]);

  // Geciken satıra git: aç + ortala. Açılma aynı karede DOM'a yansımayabilir,
  // bu yüzden kaydırma bir sonraki frame'de.
  const gitKalem = (id: number) => {
    setAcik(s2 => new Set(s2).add(id));
    requestAnimationFrame(() =>
      document.getElementById('kalem-' + id)?.scrollIntoView({ behavior: 'smooth', block: 'center' }));
  };

  const buAyMi = ay === buAy();
  const bugunGun = new Date().getDate();

  return (
    <div className={`min-h-screen ${t.bg} ${t.text} p-4 md:p-6`}>
      <div className="max-w-6xl mx-auto">
        {/* başlık + ay seçici */}
        <div className="flex items-center gap-2 mb-4 flex-wrap">
          <button onClick={() => window.location.assign('/')}
            className={`text-xs px-2.5 py-1 rounded-lg border ${t.sub} ${t.muted} hover:text-sky-400`}>
            ← panel
          </button>
          {/* LP Trax ayrı kök: sekme değil, tam sayfa yönlendirme. */}
          <button onClick={() => window.location.assign('/lptrax')}
            title="LP Trax — pozisyon bazlı LP takibi"
            className={`text-xs px-2.5 py-1 rounded-lg border ${t.sub} ${t.muted} hover:text-sky-400`}>
            lp trax
          </button>
          <button onClick={() => window.location.assign('/varliklar')}
            title="Varlıklar — fon, nakit; satınca ele geçecek tutar"
            className={`text-xs px-2.5 py-1 rounded-lg border ${t.sub} ${t.muted} hover:text-sky-400`}>
            varlıklar
          </button>
          <h1 className="text-lg font-semibold flex items-center gap-2">
            <CalendarDays size={17} className="text-sky-400" /> Bütçe
          </h1>
          <div className="flex items-center gap-1 ml-2">
            <button onClick={() => setAy(a => ayKaydir(a, -1))}
              className={`p-1 rounded-lg border ${t.sub} ${t.muted} hover:text-sky-400`}><ChevronLeft size={14} /></button>
            <span className="text-sm font-medium tabular-nums w-28 text-center">{ayEtiket(ay)}</span>
            <button onClick={() => setAy(a => ayKaydir(a, 1))}
              className={`p-1 rounded-lg border ${t.sub} ${t.muted} hover:text-sky-400`}><ChevronRight size={14} /></button>
            {!buAyMi && (
              <button onClick={() => setAy(buAy())}
                className="text-[11px] px-2 py-1 rounded-lg border border-sky-500/40 text-sky-400 hover:bg-sky-500/10">
                bu ay
              </button>
            )}
          </div>
          <div className="ml-auto flex items-center gap-2">
            <button onClick={() => setAkis(v => !v)}
              title="İleriye dönük 12 aylık akış"
              className={`text-[11px] px-2.5 py-1 rounded-lg border ${akis
                ? 'border-sky-500/50 bg-sky-500/15 text-sky-300' : `${t.sub} ${t.muted} hover:text-sky-400`}`}>
              <TrendingDown size={11} className="inline -mt-0.5" /> akış
            </button>
            <button onClick={() => setYeni(v => !v)}
              className="text-[11px] px-2.5 py-1 rounded-lg border border-sky-500/45 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20">
              <Plus size={11} className="inline -mt-0.5" /> tek seferlik
            </button>
            <button onClick={() => setAyar(v => !v)}
              className={`text-[11px] px-2.5 py-1 rounded-lg border ${ayar
                ? 'border-sky-500/50 bg-sky-500/15 text-sky-300' : `${t.sub} ${t.muted} hover:text-sky-400`}`}>
              <Settings size={11} className="inline -mt-0.5" /> kişi & şablon
            </button>
          </div>
        </div>

        {err && (
          <div className="mb-3 px-3 py-2 rounded-lg border border-rose-500/40 bg-rose-500/10 text-rose-300 text-xs flex items-center gap-2">
            {err}<button onClick={() => setErr('')} className="ml-auto"><X size={13} /></button>
          </div>
        )}
        {!connected && <p className={`text-xs ${t.muted} mb-3`}>Bağlantı bekleniyor…</p>}

        {/* GECİKEN en üstte: aksiyon gerektiren tek şey bu. Kur şeridinin
            altına koysaydık bilgi notu ile acil iş yer değiştirirdi. */}
        {(geciken.gider.length > 0 || geciken.gelir.length > 0) && (
          <GecikenSeridi geciken={geciken} t={t} git={gitKalem} />
        )}

        {d?.fx && <KurSeridi fx={d.fx} t={t} alan={alan} gonder={gonder} />}

        {/* Yaklaşan ödemeler yalnız BU AY görünürken: pencere bugüne bağlı,
            mart 2027'ye bakarken "önümüzdeki 7 gün" yanıltıcı olurdu. */}
        {buAyMi && yaklasan && (yaklasan.aySonu.satirlar.length > 0 || yaklasan.yedi.satirlar.length > 0) && (
          <YaklasanKart y={yaklasan} t={t} dark={dark}
            detay={vadeDetay} setDetay={setVadeDetay} />
        )}

        {akis && fc && (
          <AkisPaneli fc={fc} t={t} dark={dark} secPara={akisPara} setSecPara={setAkisPara} />
        )}

        {/* para birimi başına özet — her kur kendi hattında */}
        {!!d?.ozet.length && (
          <div className="grid gap-2.5 mb-4" style={{ gridTemplateColumns: `repeat(${Math.min(d.ozet.length, 3)}, minmax(0,1fr))` }}>
            {d.ozet.map(o => (
              <div key={o.para} className={`rounded-xl border ${t.card} p-3`}>
                <div className="flex items-baseline gap-2 mb-2">
                  <span className="font-semibold text-sm">{o.para}</span>
                  <span className={`text-[11px] ${t.muted}`}>net</span>
                  <span className={`ml-auto text-base font-semibold tabular-nums ${
                    o.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                    {o.net >= 0 ? '+' : '−'}{para(Math.abs(o.net), o.para)}
                  </span>
                </div>
                <div className="space-y-1 text-[11px] tabular-nums">
                  <div className="flex items-center gap-1.5">
                    <ArrowDownLeft size={11} className="text-emerald-400 shrink-0" />
                    <span className={t.muted}>gelir</span>
                    <span className="ml-auto">
                      <span className="text-emerald-400">{para(o.gelirGelen, o.para)}</span>
                      <span className={t.muted}> / {para(o.gelirBeklenen, o.para)}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <ArrowUpRight size={11} className="text-rose-400 shrink-0" />
                    <span className={t.muted}>gider</span>
                    <span className="ml-auto">
                      <span className="text-rose-400">{para(o.giderOdenen, o.para)}</span>
                      <span className={t.muted}> / {para(o.giderBeklenen, o.para)}</span>
                    </span>
                  </div>
                  <div className={`flex items-center gap-1.5 pt-1 border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                    <span className={t.muted}>ödenecek kalan</span>
                    <span className={`ml-auto font-medium ${o.giderKalan > 0 ? 'text-amber-400' : t.muted}`}>
                      {para(o.giderKalan, o.para)}
                    </span>
                  </div>
                  {/* Yalnız TRY: ödenen kısım HER ÖDEMENİN kendi kuruyla, kalan
                      güncel kurla. İki sayı aynı birimde ama farklı nitelikte —
                      biri gerçekleşmiş maliyet, biri tahmin; bu yüzden ayrı. */}
                  {/* BAKİYE: gelir/gider satırlarının ALTINDA, ayrı çizgiyle.
                      Akış değil duruş bilgisi — karıştırılmasın diye ayrı
                      blokta ve kırılımı hover'da (iki hesap tek rakamda
                      toplanınca "hangisi ne kadar" sorusu kalmasın). */}
                  {o.bakiye != null && (
                    <div className={`pt-1 mt-1 border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                      <div className="flex items-center gap-1.5 group relative cursor-default">
                        <Wallet size={11} className="text-sky-400 shrink-0" />
                        <span className={t.muted}>bakiye</span>
                        <span className="ml-auto font-medium text-sky-300">
                          {para(o.bakiye, o.para)}
                        </span>
                        {!!o.bakiyeKirilim?.length && (
                          <div className={`absolute right-0 bottom-full mb-1 z-20 hidden group-hover:block
                            rounded-lg border ${t.card} shadow-xl p-2 min-w-[15rem]`}>
                            {o.bakiyeKirilim.map((b, i) => (
                              <div key={i} className="flex items-baseline gap-2 text-[11px] py-0.5">
                                <span className="shrink-0">{b.emoji || '—'}</span>
                                <span className="truncate">{b.ad}</span>
                                <span className={`text-[10px] shrink-0 ${t.muted}`}>{b.kisi}</span>
                                <span className="ml-auto tabular-nums shrink-0">
                                  {para(b.bakiye, o.para)}
                                  <span className={`block text-[9px] leading-tight ${t.muted}`}>
                                    {b.tarih || 'tarihsiz'}{b.kaynak ? ` · ${b.kaynak}` : ''}
                                  </span>
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      {/* En eski güncelleme tarihi: bir hesap bayatsa toplam
                          da bayattır, bunu saklamıyoruz. */}
                      {!!o.bakiyeKirilim?.length && (
                        <div className={`text-[10px] ${t.muted} text-right`}>
                          {(() => {
                            const ts = o.bakiyeKirilim.map(b => b.tarih).filter(Boolean).sort();
                            return ts.length ? `son güncelleme ${ts[0]}` : 'güncelleme tarihi yok';
                          })()}
                        </div>
                      )}
                    </div>
                  )}
                  {o.usdGiderKalan != null && (
                    <div className={`flex items-center gap-1.5 ${t.muted}`}>
                      <span>USDC karşılığı</span>
                      <span className="ml-auto">
                        <span className="text-sky-300" title="ödenen — her ödemenin kendi kuruyla">
                          {usdc(o.usdGiderOdenen)}
                        </span>
                        {(o.usdGiderKalan || 0) > 0 && (
                          <span title="kalan — güncel kurla tahmin"> + {usdc(o.usdGiderKalan)}</span>
                        )}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ekstresi girilmemiş kartlar */}
        {!!d?.bekleyen.length && <BekleyenKartlar d={d} t={t} alan={alan} gonder={gonder} />}

        {yeni && <TekSeferlik d={d} t={t} alan={alan} ay={ay}
          kapat={() => setYeni(false)} gonder={gonder} />}

        {ayar && <Ayarlar d={d} t={t} alan={alan} gonder={gonder} />}

        {/* gün gün akan liste — solda gider, sağda gelir */}
        {d && kalemSayisi === 0 && (
          <p className={`text-sm ${t.muted} py-10 text-center`}>
            Bu ayda kalem yok. Şablon tanımla (kira, kredi, maaş) ya da tek seferlik bir kalem ekle.
          </p>
        )}
        {kalemSayisi > 0 && (
          <div className="grid gap-x-4 gap-y-2 lg:grid-cols-2 items-start">
            {([['gider', 'Giderler'], ['gelir', 'Gelirler']] as const).map(([yon, baslik]) => (
              <section key={yon}>
                <div className={`flex items-center gap-1.5 px-1 pb-1.5 mb-0.5 border-b ${
                  dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'}`}>
                  {yon === 'gider'
                    ? <ArrowUpRight size={13} className="text-rose-400" />
                    : <ArrowDownLeft size={13} className="text-emerald-400" />}
                  <span className="text-xs font-semibold">{baslik}</span>
                  <span className={`text-[10px] ${t.muted}`}>
                    {kolonlar[yon].reduce((n, g) => n + g.kalemler.length, 0)} kalem
                  </span>
                </div>
                {kolonlar[yon].length === 0 && (
                  <p className={`text-[11px] ${t.muted} py-4 px-1`}>
                    {yon === 'gider' ? 'Bu ayda gider kalemi yok.' : 'Bu ayda gelir kalemi yok.'}
                  </p>
                )}
                <div className="space-y-1.5">
                  {kolonlar[yon].map(({ gun, kalemler }) => (
                    <div key={gun}>
                      <div className={`flex items-center gap-2 px-1 mb-1 mt-3 text-[11px] ${t.muted}`}>
                        <span className={`font-semibold tabular-nums ${buAyMi && gun === bugunGun ? 'text-sky-400' : ''}`}>
                          {String(gun).padStart(2, '0')}
                        </span>
                        <span className={`flex-1 h-px ${dark ? 'bg-[#0a2560]' : 'bg-[#d4e0f0]'}`} />
                        {buAyMi && gun < bugunGun && <span className="text-[10px] opacity-60">geçti</span>}
                      </div>
                      {kalemler.map(k => (
                        <KalemSatiri key={k.id} k={k} t={t} alan={alan} dark={dark} kisi={kisi}
                          kisiler={d?.kisiler || []} gonder={gonder} fx={d?.fx}
                          acik={acik.has(k.id)} secili={sec.has(k.id)}
                          sec={() => setSec(s2 => {
                            const n = new Set(s2); n.has(k.id) ? n.delete(k.id) : n.add(k.id); return n;
                          })}
                          topla={() => setAcik(s => {
                            const n = new Set(s); n.has(k.id) ? n.delete(k.id) : n.add(k.id); return n;
                          })} />
                      ))}
                    </div>
                  ))}
                </div>
              </section>
            ))}
          </div>
        )}

        {/* Şerit sabit, en alttaki satırın üstüne binmesin diye boşluk. */}
        {secili.length > 0 && <div className="h-28" />}
      </div>

      {secili.length > 0 && (
        <SecimSeridi secili={secili} t={t} dark={dark}
          ac={() => setDetay(true)} temizle={() => { setSec(new Set()); setDetay(false); }} />
      )}
      {detay && secili.length > 0 && (
        <ToplamModal secili={secili} t={t} dark={dark} kapat={() => setDetay(false)} />
      )}
    </div>
  );
}

// ── seçim şeridi ─────────────────────────────────────────────────────────────
// Pop-up yerine CANLI şerit: seçerken sayının anında oynaması, "toplamı göster"
// tuşuna basıp beklemekten kullanışlı. Tuş kalktı değil, kırılımı açıyor —
// şeritte yalnız manşet var, dökümü modal veriyor.
function SecimSeridi({ secili, t, dark, ac, temizle }: any) {
  const toplamlar = useMemo(() => secimToplam(secili), [secili]);
  return (
    <div className={`fixed bottom-0 left-0 right-0 z-30 border-t backdrop-blur-sm ${
      dark ? 'bg-[#061530]/95 border-[#164a9e]' : 'bg-white/95 border-[#d4e0f0]'}`}>
      <div className="max-w-6xl mx-auto px-4 py-2.5 flex items-center gap-3 flex-wrap">
        <span className="text-xs font-semibold text-sky-300 flex items-center gap-1.5 shrink-0">
          <Sigma size={13} /> {secili.length} kalem seçildi
        </span>
        <div className="flex items-center gap-3 flex-wrap text-[11px] tabular-nums">
          {toplamlar.map(o => {
            // Karma seçimde (gelir+gider) tek "toplam" anlamsız → NET basılıyor.
            const karma = o.gider.adet > 0 && o.gelir.adet > 0;
            const y = o.gider.adet > 0 ? o.gider : o.gelir;
            const ad = o.gider.adet > 0 ? 'ödenen' : 'gelen';
            return (
              <span key={o.para} className={`px-2 py-1 rounded-lg border ${t.sub}`}>
                <span className={`${t.muted} mr-1.5`}>{o.para}</span>
                {karma ? (
                  <>
                    <span className={o.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                      net {o.net >= 0 ? '+' : '−'}{para(Math.abs(o.net), o.para)}
                    </span>
                    <span className={t.muted}> · gerçekleşen {o.netGercek >= 0 ? '+' : '−'}
                      {para(Math.abs(o.netGercek), o.para)}</span>
                  </>
                ) : (
                  <>
                    <span className="font-medium">{para(y.tutar, o.para)}</span>
                    <span className={t.muted}> · {ad} {para(y.gercek, o.para)} · kalan{' '}
                      {para(y.kalan, o.para)}</span>
                  </>
                )}
              </span>
            );
          })}
        </div>
        <div className="ml-auto flex items-center gap-2 shrink-0">
          <button onClick={ac}
            className="text-[11px] px-2.5 py-1.5 rounded-lg border border-sky-500/45 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20">
            toplamı göster
          </button>
          <button onClick={temizle}
            className={`text-[11px] px-2.5 py-1.5 rounded-lg border ${t.sub} ${t.muted} hover:text-rose-400`}>
            temizle
          </button>
        </div>
      </div>
    </div>
  );
}

// ── toplam dökümü ────────────────────────────────────────────────────────────
function ToplamModal({ secili, t, dark, kapat }: any) {
  const toplamlar = useMemo(() => secimToplam(secili), [secili]);
  const cizgi = dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]';

  const yonBlok = (etiket: string, y: YonToplam, o: ParaToplam, renk: string, gercekAd: string) =>
    y.adet === 0 ? null : (
      <div className="pt-1">
        <div className="flex items-baseline gap-2 text-[11px] tabular-nums">
          <span className={`${t.muted} w-11 shrink-0`}>{etiket}</span>
          <span className={`${renk} font-medium`}>{para(y.tutar, o.para)}</span>
          <span className={t.muted}>· {gercekAd} {para(y.gercek, o.para)}</span>
          <span className={`ml-auto ${y.kalan > 0 ? 'text-amber-400' : t.muted}`}>
            kalan {para(y.kalan, o.para)}
          </span>
        </div>
        {/* USDC karşılığı: gerçekleşen kısım kesin (donmuş kur), kalan tahmin
            (güncel kur). Bilerek tek rakamda toplanmıyor. */}
        {y.usdVar && (
          <div className={`flex items-baseline gap-2 pl-[3.25rem] text-[10px] ${t.muted} tabular-nums`}>
            <span className="text-sky-300" title={`${gercekAd} — her ödemenin kendi donmuş kuruyla, gerçekleşmiş`}>
              {usdc(y.usdGercek)}
            </span>
            {y.usdKalan > 0 && (
              <span title="kalan — bugünkü kurla tahmin">+ {usdc(y.usdKalan)} tahmin</span>
            )}
            {y.kurYok && (
              <span className="text-amber-400 flex items-center gap-1">
                <AlertTriangle size={9} /> kuru olmayan ödeme var
              </span>
            )}
          </div>
        )}
      </div>
    );

  return (
    <div className="fixed inset-0 z-40 bg-black/60 flex items-center justify-center p-4"
      onClick={kapat}>
      <div onClick={e => e.stopPropagation()}
        className={`w-full max-w-lg max-h-[82vh] overflow-auto rounded-xl border ${t.card} p-4`}>
        <div className="flex items-center gap-2 mb-3">
          <Sigma size={15} className="text-sky-400" />
          <span className="text-sm font-semibold">{secili.length} kalemin toplamı</span>
          <button onClick={kapat} className={`ml-auto ${t.muted} hover:text-rose-400`}><X size={15} /></button>
        </div>

        <div className="space-y-2">
          {toplamlar.map(o => (
            <div key={o.para} className={`rounded-lg border ${t.sub} px-2.5 py-2`}>
              <div className="flex items-baseline gap-2">
                <span className="text-xs font-semibold">{o.para}</span>
                <span className={`text-[10px] ${t.muted}`}>{o.adet} kalem</span>
                {o.gider.adet > 0 && o.gelir.adet > 0 && (
                  <span className={`ml-auto text-xs font-semibold tabular-nums ${
                    o.net >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}
                    title="gelir toplamı − gider toplamı">
                    net {o.net >= 0 ? '+' : '−'}{para(Math.abs(o.net), o.para)}
                  </span>
                )}
              </div>
              {yonBlok('gider', o.gider, o, 'text-rose-400', 'ödenen')}
              {yonBlok('gelir', o.gelir, o, 'text-emerald-400', 'gelen')}
              {o.gider.adet > 0 && o.gelir.adet > 0 && (
                <div className={`flex items-baseline gap-2 mt-1.5 pt-1.5 border-t ${cizgi} text-[11px] tabular-nums`}>
                  <span className={`${t.muted} w-11 shrink-0`}>gerçek</span>
                  <span className={o.netGercek >= 0 ? 'text-emerald-400' : 'text-rose-400'}>
                    {o.netGercek >= 0 ? '+' : '−'}{para(Math.abs(o.netGercek), o.para)}
                  </span>
                  <span className={`ml-auto ${t.muted}`}>gelen − ödenen</span>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Seçimin kendisi de görünsün: hangi kalemler toplandı sorusu, rakamın
            yanında cevabını bulmalı. */}
        <div className={`mt-3 pt-2 border-t ${cizgi} space-y-1`}>
          {secili.map((k: Kalem) => {
            // Satır başına ≈$ de ÜSTTEKİ BLOKLA AYNI KURALLA: ödenmiş kısım
            // kendi donmuş kuruyla, kalan bugünkü kurla. Kur alanı olmayan para
            // biriminde (EUR) hiç basılmıyor — uydurma sayı çıkmasın.
            const usdVar = k.usdTutar != null || (k.usdOdenen || 0) > 0 || k.usdKalan != null;
            return (
              <div key={k.id} className="flex items-baseline gap-2 text-[11px]">
                {k.yon === 'gelir'
                  ? <ArrowDownLeft size={10} className="text-emerald-400 shrink-0" />
                  : <ArrowUpRight size={10} className="text-rose-400 shrink-0" />}
                <span className="truncate">{k.ad}</span>
                <span className={`text-[10px] ${t.muted} shrink-0`}>
                  {String(gunu(k.vade)).padStart(2, '0')}
                </span>
                <span className="ml-auto tabular-nums shrink-0 text-right">
                  {para(k.tutar, k.para)}
                  {usdVar && (
                    <span className={`block text-[10px] leading-tight ${t.muted}`}
                      title={k.odenen > 0
                        ? `ödenen ${usdc(k.usdOdenen)} (kendi kuruyla) + kalan ${usdc(k.usdKalan)} (güncel kur)`
                        : 'güncel kurla tahmin'}>
                      {k.odenen > 0
                        ? `${usdc(k.usdOdenen)}${(k.usdKalan || 0) > 0 ? ` + ${usdc(k.usdKalan)}` : ''}`
                        : usdc(k.usdTutar)}
                      {k.usdKurYok && <AlertTriangle size={9} className="inline ml-1 -mt-0.5 text-amber-400" />}
                    </span>
                  )}
                </span>
                <span className={`tabular-nums shrink-0 w-28 text-right ${
                  k.durum === 'odendi' ? 'text-emerald-400' : k.durum === 'kismi' ? 'text-amber-400' : t.muted}`}>
                  {k.durum === 'odendi' ? 'ödendi' : `kalan ${para(k.kalan, k.para)}`}
                  {usdVar && k.durum !== 'odendi' && (k.usdKalan || 0) > 0 && (
                    <span className={`block text-[10px] leading-tight ${t.muted}`}
                      title="kalan — bugünkü kurla tahmin">
                      {usdc(k.usdKalan)}
                    </span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ── geciken kalemler ─────────────────────────────────────────────────────────
// Vadesi geçmiş ve kalanı olan satırlar. Gün başlığındaki "geçti" etiketi vardı
// ama listeyi taramayı gerektiriyordu; burada tek bakışta ve TIKLANABİLİR —
// isme basınca kalem açılıp ortalanıyor, ödeme girmek iki tık uzakta.
function GecikenSeridi({ geciken, t, git }: any) {
  const kutu = (ks: Kalem[], baslik: string, renk: string, ikon: any) => {
    if (!ks.length) return null;
    // Toplam para birimi başına: tek rakam basmak uydurma kura bağlı olurdu.
    const by: Record<string, number> = {};
    for (const k of ks) by[k.para] = round2((by[k.para] || 0) + k.kalan);
    return (
      <div className="flex items-center gap-2 flex-wrap">
        <span className={`text-xs font-semibold flex items-center gap-1.5 shrink-0 ${renk}`}>
          {ikon} {ks.length} {baslik}
        </span>
        <span className="text-[11px] tabular-nums">
          {Object.entries(by).map(([pr, v], i) => (
            <span key={pr}>{i > 0 && <span className={t.muted}> · </span>}{para(v, pr)}</span>
          ))}
        </span>
        <span className="flex items-center gap-1.5 flex-wrap">
          {ks.map(k => (
            <button key={k.id} onClick={() => git(k.id)}
              title={`${k.ad} · vade ${k.vade} · kalan ${para(k.kalan, k.para)}`}
              className={`text-[10px] px-1.5 py-0.5 rounded border ${t.sub} hover:text-sky-400 hover:border-sky-500/40`}>
              {k.ad}
              <span className={`ml-1 ${t.muted}`}>{Math.abs(gunFark(k.vade))}g</span>
            </button>
          ))}
        </span>
      </div>
    );
  };
  return (
    <div className="mb-3 px-3 py-2 rounded-xl border border-rose-500/40 bg-rose-500/10 space-y-1.5">
      {kutu(geciken.gider, 'ödeme gecikti', 'text-rose-300',
            <AlertTriangle size={13} className="inline -mt-0.5" />)}
      {kutu(geciken.gelir, 'tahsilat gecikti', 'text-amber-300',
            <Clock size={13} className="inline -mt-0.5" />)}
    </div>
  );
}

// ── yaklaşan ödemeler ────────────────────────────────────────────────────────
// Cevapladığı soru: "önümüzdeki 7 / 30 günde kaç USDC hazırlamam lazım."
// TRY satırları bugünkü kurla tahmin — satış henüz yapılmadı, doğrusu da bu.
// Projeksiyon satırları (henüz açılmamış ayın şablon satırı) ayrıca işaretli:
// kesin bir borç değil, beklenen bir borç.
function YaklasanKart({ y, t, dark, detay, setDetay }: any) {
  const blok = (p: any, baslik: string, vurgu: boolean) => (
    <div className={`rounded-lg border ${t.sub} p-2.5 min-w-0 ${vurgu ? 'ring-1 ring-sky-500/30' : ''}`}>
      <div className={`text-[10px] uppercase tracking-wide ${t.muted} mb-1`}>{baslik}</div>
      {p.satirlar.length === 0 ? <div className={`text-xs ${t.muted}`}>—</div> : (
        <>
          <div className="text-sm font-semibold tabular-nums text-sky-300 mb-0.5"
            title="bugünkü kurla tahmin — Binance TR'de satılacak USDC">
            {p.usd == null ? '—' : usdc(p.usd)}
          </div>
          <div className={`text-[11px] tabular-nums ${t.muted}`}>
            {Object.entries(p.paralar).map(([pr, v], i) => (
              <span key={pr}>{i > 0 && ' · '}{para(v as number, pr)}</span>
            ))}
          </div>
          <div className={`text-[10px] ${t.muted} mt-0.5`}>{p.satirlar.length} kalem</div>
        </>
      )}
    </div>
  );
  return (
    <div className={`rounded-xl border ${t.card} p-3 mb-4`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-xs font-semibold flex items-center gap-1.5">
          <CalendarClock size={13} className="text-sky-400" /> Yaklaşan ödemeler
        </span>
        <span className={`text-[10px] ${t.muted}`}>bugünden itibaren · yalnız ödenmemiş kalan</span>
        <span className={`text-[10px] ${t.muted}`}>ay sonu: {y.sonGun.slice(8)}.{y.sonGun.slice(5, 7)}</span>
        <button onClick={() => setDetay((v: boolean) => !v)}
          className={`ml-auto text-[10px] px-2 py-1 rounded-lg border ${t.sub} ${t.muted} hover:text-sky-400`}>
          {detay ? 'gizle' : 'dökümü göster'}
        </button>
      </div>
      <div className="grid grid-cols-2 gap-2">
        {blok(y.yedi, '7 gün', false)}
        {blok(y.aySonu, 'ay sonuna kadar', true)}
      </div>
      {detay && (
        <div className={`mt-2 pt-2 border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'} space-y-1`}>
          {y.aySonu.satirlar.map((r: FcVade, i: number) => {
            const g = gunFark(r.vade);
            return (
              <div key={`${r.kalemId}-${r.vade}-${i}`} className="flex items-baseline gap-2 text-[11px]">
                <span className={`tabular-nums w-10 shrink-0 ${g <= 2 ? 'text-amber-400' : t.muted}`}>
                  {g === 0 ? 'bugün' : `${g}g`}
                </span>
                <span className="truncate">{r.ad}</span>
                {r.projeksiyon && (
                  <span className={`text-[9px] px-1 py-0.5 rounded border ${t.sub} ${t.muted} shrink-0`}
                    title="henüz açılmamış ayın şablon satırı — beklenen borç">
                    projeksiyon
                  </span>
                )}
                <span className="ml-auto tabular-nums shrink-0 text-right">
                  {para(r.tutar, r.para)}
                  {r.usd != null && (
                    <span className={`block text-[10px] leading-tight ${t.muted}`}>{usdc(r.usd)}</span>
                  )}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── 12 aylık akış ────────────────────────────────────────────────────────────
// SABİT ve DEĞİŞKEN AYRI ÇİZİLİR. Kart ekstresi her ay elle giriliyor, gelecek
// ayın karşılığı yok; tek çubukta toplasaydık gelecek aylar sahte bir rahatlama
// gösterirdi (ölçüm: kasım TRY giderinin %70'i ekstre). Girilmemiş ay için
// rakam uydurmuyoruz, "ekstre yok" yazıyoruz.
function AkisPaneli({ fc, t, dark, secPara, setSecPara }: any) {
  const paralar = useMemo(() => {
    const set = new Set<string>();
    for (const a of fc.aylar) for (const p of a.paralar) set.add(p.para);
    return Array.from(set).sort();
  }, [fc]);
  const pr = secPara && paralar.includes(secPara) ? secPara : (paralar[0] || '');
  const satirlar = useMemo(() => fc.aylar.map((a: FcAy) => ({
    ay: a.ay, biten: a.biten, baslayan: a.baslayan,
    p: a.paralar.find((x: FcPara) => x.para === pr),
  })), [fc, pr]);
  const enBuyuk = Math.max(1, ...satirlar.map((r: any) => (r.p?.gider || 0)));
  const buAyS = buAy();
  const cizgi = dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]';

  return (
    <div className={`rounded-xl border ${t.card} p-3 mb-4`}>
      <div className="flex items-center gap-2 mb-2 flex-wrap">
        <span className="text-xs font-semibold flex items-center gap-1.5">
          <TrendingDown size={13} className="text-sky-400" /> İleriye dönük akış · 12 ay
        </span>
        <span className={`text-[10px] ${t.muted}`}>şablonlardan projeksiyon · satır üretmez</span>
        <div className="ml-auto flex items-center gap-1">
          {paralar.map((x: string) => (
            <button key={x} onClick={() => setSecPara(x)}
              className={`text-[10px] px-2 py-1 rounded-lg border ${x === pr
                ? 'border-sky-500/50 bg-sky-500/15 text-sky-300' : `${t.sub} ${t.muted} hover:text-sky-400`}`}>
              {x}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-1">
        {satirlar.map((r: any) => {
          const p: FcPara | undefined = r.p;
          const sabit = p?.giderSabit || 0, degisken = p?.giderDegisken || 0;
          const gelecek = r.ay > buAyS;
          return (
            <div key={r.ay} className={`rounded-lg px-2 py-1.5 ${r.ay === buAyS ? `border ${cizgi}` : ''}`}>
              <div className="flex items-center gap-2 text-[11px]">
                <span className={`font-semibold tabular-nums w-14 shrink-0 ${
                  r.ay === buAyS ? 'text-sky-400' : ''}`}>{ayKisa(r.ay)}</span>
                {/* Çubuk: koyu mavi sabit yük, mor kart/tek seferlik. */}
                <span className="flex-1 min-w-0 h-3 rounded bg-black/20 overflow-hidden flex">
                  <span className="h-full bg-sky-500/70"
                    style={{ width: `${(sabit / enBuyuk) * 100}%` }} title={`sabit ${para(sabit, pr)}`} />
                  <span className="h-full bg-violet-500/70"
                    style={{ width: `${(degisken / enBuyuk) * 100}%` }}
                    title={`tek seferlik / ekstre ${para(degisken, pr)}`} />
                </span>
                <span className="tabular-nums shrink-0 w-28 text-right">
                  {para(sabit + degisken, pr)}
                  {p?.usdGider != null && (
                    <span className={`block text-[10px] leading-tight ${t.muted}`}>{usdc(p.usdGider)}</span>
                  )}
                </span>
                <span className={`tabular-nums shrink-0 w-24 text-right ${
                  (p?.gelir || 0) > 0 ? ((p?.net || 0) >= 0 ? 'text-emerald-400' : 'text-rose-400') : t.muted}`}>
                  {(p?.gelir || 0) > 0
                    ? `net ${(p?.net || 0) >= 0 ? '+' : '−'}${para(Math.abs(p?.net || 0), pr)}`
                    : '—'}
                </span>
              </div>
              {/* İşaretler ve "ekstre girilmedi" uyarısı aynı satırda. */}
              {(r.biten.length > 0 || r.baslayan.length > 0 || (gelecek && !p?.degiskenVar)) && (
                <div className="flex items-center gap-1.5 flex-wrap pl-16 mt-0.5">
                  {r.biten.map((b: FcIsaret) => (
                    <span key={b.ad}
                      className="text-[10px] px-1.5 py-0.5 rounded border border-emerald-500/30 bg-emerald-500/10 text-emerald-300"
                      title="bu aydan itibaren aylık yük düşüyor">
                      {b.ad} bitti · −{para(b.tutar, b.para)}/ay
                    </span>
                  ))}
                  {r.baslayan.map((b: FcIsaret) => (
                    <span key={b.ad}
                      className="text-[10px] px-1.5 py-0.5 rounded border border-amber-500/30 bg-amber-500/10 text-amber-300">
                      {b.ad} başlıyor · +{para(b.tutar, b.para)}/ay
                    </span>
                  ))}
                  {gelecek && !p?.degiskenVar && (
                    <span className={`text-[10px] px-1.5 py-0.5 rounded border ${t.sub} ${t.muted}`}
                      title="kredi kartı ekstresi her ay elle giriliyor — bu ayınki henüz yok, projeksiyona dahil DEĞİL">
                      ekstre girilmedi
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {fc.krediler.length > 0 && (
        <div className={`mt-2 pt-2 border-t ${cizgi} flex items-center gap-2 flex-wrap`}>
          <span className={`text-[10px] uppercase tracking-wide ${t.muted}`}>krediler</span>
          {fc.krediler.map((k: FcKredi) => (
            <span key={k.ad} className={`text-[11px] px-2 py-1 rounded-lg border ${t.sub}`}
              title={`son taksit ${k.sonAy}`}>
              {k.ad} <span className="text-sky-300">{k.taksit} taksit</span>
              <span className={t.muted}> · kalan {para(k.kalanBorc, k.para)}</span>
            </span>
          ))}
        </div>
      )}
      <p className={`text-[10px] ${t.muted} mt-2`}>
        Çubuk: <span className="text-sky-300">sabit yük</span> (şablon) +{' '}
        <span className="text-violet-300">tek seferlik / kart ekstresi</span>. Ekstre her ay elle
        girildiği için gelecek aylara DAHİL DEĞİL — o aylar olduğundan hafif görünür.
      </p>
    </div>
  );
}

// ── kur şeridi (TRY → USDC) ──────────────────────────────────────────────────
// Neden burada: TRY ödemeleri kriptodan gelen USDC'nin Binance TR'de satılmasıyla
// yapılıyor, satırın yanındaki "≈$" bu kurdan geliyor. Kur ÇEKİLİNCE yalnız
// ÖDENMEMİŞ kalanların tahmini değişir; ödenmiş satırlar kendi kurunu saklıyor.
//
// Çekme ucu: binance.tr open/v1 market/depth (API key istemiyor). Otomatik
// döngü YOK — kullanıcı kararı: ne zaman satış yapacaksa o zaman çekiyor.
function KurSeridi({ fx, t, alan, gonder }: any) {
  const [bid, setBid] = useState('');
  const [kom, setKom] = useState('');
  const [bekle, setBekle] = useState(false);

  // Sunucudan yeni kur gelince düzenleme alanları boşaltılır: aksi halde elle
  // yazılmış eski değer ekranda kalıp çekilen kuru gizliyordu.
  useEffect(() => { setBid(''); setKom(''); setBekle(false); }, [fx.bid, fx.kom, fx.ts]);

  const yaz = () => {
    const b = bid.trim() ? parseFloat(bid.replace(',', '.')) : null;
    const k = kom.trim() ? parseFloat(kom.replace(',', '.')) : null;
    if (b == null && k == null) return;
    gonder('budget_save_fx', { bid: b, kom: k });
  };
  const cek = () => { setBekle(true); gonder('budget_fx_fetch', {}); };

  const degisik = !!bid.trim() || !!kom.trim();
  return (
    <div className={`rounded-xl border ${t.card} px-3 py-2 mb-4 flex items-center gap-2 flex-wrap text-xs`}>
      <span className="font-semibold">USDC/{fx.para}</span>
      {fx.bid > 0 ? (
        <>
          <span className="tabular-nums" title="Binance TR alış (bid)">{fx.bid}</span>
          <span className={t.muted}>− %{fx.kom} →</span>
          <span className="tabular-nums text-sky-300 font-medium" title="komisyon düşülmüş efektif kur">
            {fx.efektif}
          </span>
          <span className={`text-[10px] ${t.muted}`}>
            {fx.kaynak === 'elle' ? 'elle girildi' : fx.kaynak} · {kurZaman(fx.ts)}
          </span>
        </>
      ) : (
        <span className="text-amber-400 text-[11px]">
          kur yok — TRY satırlarının USDC karşılığı gösterilemiyor
        </span>
      )}
      <div className="ml-auto flex items-center gap-1.5">
        <input value={bid} onChange={e => setBid(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') yaz(); }}
          placeholder="bid elle" inputMode="decimal"
          className={`${alan} w-24 tabular-nums`} />
        <input value={kom} onChange={e => setKom(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') yaz(); }}
          placeholder={`kom %${fx.kom}`} inputMode="decimal"
          className={`${alan} w-20 tabular-nums`} />
        {degisik && (
          <button onClick={yaz}
            className="px-2 py-1.5 rounded-lg text-[11px] border border-sky-500/45 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20">
            kaydet
          </button>
        )}
        <button onClick={cek} disabled={bekle}
          title="Binance TR emir defterinden güncel alış kurunu çek"
          className={`flex items-center gap-1 px-2 py-1.5 rounded-lg text-[11px] border ${t.sub} ${t.muted} hover:text-sky-400 disabled:opacity-50`}>
          <RefreshCw size={11} className={bekle ? 'animate-spin' : ''} /> kuru çek
        </button>
      </div>
    </div>
  );
}

// ── ekstresi bekleyen kartlar ────────────────────────────────────────────────
// Kart şablonu ad/para/günü taşıyor, tutarı her ay elle giriliyor. Böylece kart
// her ay yeniden tanımlanmıyor; yalnız ekstre tutarı yazılıyor.
function BekleyenKartlar({ d, t, alan, gonder }: any) {
  const [tutar, setTutar] = useState<Record<number, string>>({});
  const [asg, setAsg]     = useState<Record<number, string>>({});
  const gir = (s: Sablon) => {
    const v = parseFloat((tutar[s.id] || '').replace(',', '.'));
    if (!(v > 0)) return;
    gonder('budget_save_kalem', {
      sablonId: s.id, kisiId: s.kisi_id, ad: s.ad, yon: s.yon, tur: s.tur,
      para: s.para, tutar: v, vade: s.vade,
      asgari: parseFloat((asg[s.id] || '').replace(',', '.')) || 0,
    });
    setTutar(p => ({ ...p, [s.id]: '' }));
    setAsg(p => ({ ...p, [s.id]: '' }));
  };
  return (
    <div className={`rounded-xl border ${t.card} p-3 mb-4`}>
      <div className={`text-[11px] ${t.muted} mb-2`}>
        Ekstresi girilmemiş ({d.bekleyen.length}) — tutarı yaz, ay listesine düşsün.
        Asgari boş bırakılabilir, sonradan da girilebilir.
      </div>
      <div className="space-y-1.5">
        {d.bekleyen.map((s: Sablon) => (
          <div key={s.id} className={`flex items-center gap-2 text-xs px-2 py-1.5 rounded-lg border ${t.sub}`}>
            <span className={`tabular-nums w-6 ${t.muted}`}>{String(s.gun).padStart(2, '0')}</span>
            <span className="font-medium">{s.ad}</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded ${t.muted} border ${t.sub}`}>{s.para}</span>
            <input value={tutar[s.id] || ''} onChange={e => setTutar(p => ({ ...p, [s.id]: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') gir(s); }}
              placeholder="ekstre tutarı" inputMode="decimal"
              className={`${alan} ml-auto w-32 tabular-nums`} />
            <input value={asg[s.id] || ''} onChange={e => setAsg(p => ({ ...p, [s.id]: e.target.value }))}
              onKeyDown={e => { if (e.key === 'Enter') gir(s); }}
              placeholder="asgari" inputMode="decimal" title="asgari ödeme tutarı (zorunlu değil)"
              className={`${alan} w-24 tabular-nums`} />
            <button onClick={() => gir(s)}
              className="px-2 py-1 rounded-lg text-[11px] border border-sky-500/45 bg-sky-500/10 text-sky-300 hover:bg-sky-500/20">
              ekle
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── tek bir kalem satırı + parça ödemeleri ───────────────────────────────────
function KalemSatiri({ k, t, alan, dark, kisi, kisiler, gonder, acik, topla, fx, secili, sec }: any) {
  const [tutar, setTutar] = useState('');
  const [tarih, setTarih] = useState(bugun());
  const [kid, setKid]     = useState<number>(k.kisiId || kisiler[0]?.id || 0);
  // Bu ödemenin kuru. Ayardaki güncel bid ile açılıyor; o sabah başka fiyattan
  // satıldıysa elle değiştirilebiliyor ve SATIRA o yazılıyor (geri dönük
  // düzeltme yok — ödeme kaydedildikten sonra kuru kendi satırında donuyor).
  const fxli = !!fx && k.para === fx.para;
  const [bid, setBid] = useState('');
  useEffect(() => { if (fxli) setBid(fx.bid ? String(fx.bid) : ''); }, [fxli, fx?.bid]);

  const gelir = k.yon === 'gelir';
  const renk = k.durum === 'odendi' ? 'text-emerald-400'
    : k.durum === 'kismi' ? 'text-amber-400' : t.muted;
  const sahip = kisi(k.kisiId);

  const kurAl = () => (fxli ? parseFloat((bid || '').replace(',', '.')) || 0 : 0);
  const ode = () => {
    const v = parseFloat(tutar.replace(',', '.'));
    if (!(v > 0)) return;
    gonder('budget_save_hareket', { kalemId: k.id, tutar: v, tarih, kisiId: kid, kur: kurAl() });
    setTutar('');
  };
  const tamami = () => {
    if (!(k.kalan > 0)) return;
    gonder('budget_save_hareket', { kalemId: k.id, tutar: k.kalan, tarih, kisiId: kid, kur: kurAl() });
  };

  return (
    <div id={`kalem-${k.id}`}
      className={`rounded-lg border ${t.card} ${k.durum === 'odendi' ? 'opacity-70' : ''} ${
        secili ? 'ring-1 ring-sky-500/60' : ''}`}>
      <div className="flex items-center gap-2 text-xs px-2.5 py-2 cursor-pointer" onClick={topla}>
        {/* SEÇİM KUTUSU AYRI. Satırın kendi tıklaması kalemi AÇIYOR (ödeme
            girmek için); seçim onun üstüne binmesin diye kendi kutusu ve
            stopPropagation var. Aynı tıklamaya bindirsek ödeme girmek için
            açtığın her satır toplama da girerdi. */}
        <button onClick={e => { e.stopPropagation(); sec?.(); }}
          title={secili ? 'seçimden çıkar' : 'toplama ekle'}
          className={`shrink-0 w-[15px] h-[15px] rounded border flex items-center justify-center transition ${
            secili ? 'bg-sky-500 border-sky-400 text-white'
                   : `${t.sub} ${t.muted} hover:border-sky-400`}`}>
          {secili && <Check size={10} strokeWidth={3} />}
        </button>
        <span title={sahip?.ad || 'sahipsiz'} className="text-sm shrink-0 w-5 text-center">
          {sahip?.emoji || '—'}
        </span>
        {gelir ? <ArrowDownLeft size={12} className="text-emerald-400 shrink-0" />
               : <ArrowUpRight size={12} className="text-rose-400 shrink-0" />}
        <span className="font-medium truncate">{k.ad}</span>
        <span className={`text-[10px] px-1.5 py-0.5 rounded border ${t.sub} ${t.muted} shrink-0 hidden sm:inline`}>
          {TUR_AD[k.tur] || k.tur}
        </span>
        {k.sablonId === 0 && (
          <span className="text-[10px] px-1.5 py-0.5 rounded border border-violet-500/30 bg-violet-500/10 text-violet-300 shrink-0 hidden md:inline">
            tek seferlik
          </span>
        )}
        <span className="ml-auto tabular-nums shrink-0 text-right">
          {para(k.tutar, k.para)}
          {/* TRY satırının yaklaşık USDC maliyeti: ödenmiş kısım kendi kuruyla,
              kalan güncel kurla. Hiç ödeme yoksa tutarın tamamı tahmin. */}
          {fxli && (k.usdTutar != null || (k.usdOdenen || 0) > 0) && (
            <span className={`block text-[10px] leading-tight ${t.muted}`}
              title={k.odenen > 0
                ? `ödenen ${usdc(k.usdOdenen)} (kendi kuruyla) + kalan ${usdc(k.usdKalan)} (güncel kur)`
                : 'güncel kurla tahmin'}>
              {k.odenen > 0
                ? `${usdc(k.usdOdenen)}${(k.usdKalan || 0) > 0 ? ` + ${usdc(k.usdKalan)}` : ''}`
                : usdc(k.usdTutar)}
              {k.usdKurYok && <AlertTriangle size={9} className="inline ml-1 -mt-0.5 text-amber-400" />}
            </span>
          )}
        </span>
        {/* Durum hücresi iki satır: üstte nerede olduğumuz, altta ne kaldığı.
            ASGARİ ÖDENDİ, "ödendi" ile karıştırılmasın diye ayrı renkte ve
            altında kalan borç duruyor — kartın kapanmadığı görünür kalıyor. */}
        <span className={`tabular-nums shrink-0 w-32 text-right ${renk}`}>
          {/* OTOMATİK YATTI ≠ TAHSİL EDİLDİ: rakam kayıttan değil tikten
              geliyor, ayrı yazılıyor ki ölçülen para ile varsayılan karışmasın. */}
          {k.otoTahsilEdildi ? (
            <span className="text-emerald-400" title="otomatik yatar işaretli — elle kayıt girilmedi">
              <Check size={11} className="inline -mt-0.5" /> otomatik yattı
            </span>
          ) : k.durum === 'odendi' ? <><Check size={11} className="inline -mt-0.5" /> ödendi</>
            : k.otoTahsil ? <span className={t.muted}>otomatik · {k.vade.slice(8)}.{k.vade.slice(5, 7)}</span>
            : k.asgariOdendi ? <span className="text-emerald-400">asgari ödendi</span>
            : k.odenen > 0 ? `${para(k.odenen, k.para)} ödendi`
            : <span className={t.muted}>kalan {para(k.kalan, k.para)}</span>}
          {k.durum !== 'odendi' && !k.otoTahsil && (k.asgariOdendi || k.asgari > 0 || k.odenen > 0) && (
            <span className={`block text-[10px] leading-tight ${t.muted}`}>
              {k.asgariOdendi || k.asgari === 0
                ? `kalan ${para(k.kalan, k.para)}`
                : `asgariye ${para(k.asgariKalan, k.para)}`}
            </span>
          )}
        </span>
      </div>

      {acik && (
        <div className={`border-t ${dark ? 'border-[#0a2560]' : 'border-[#d4e0f0]'} px-2.5 py-2 space-y-1.5`}>
          {/* Asgari yalnız kartlarda sorulur; zaten girilmişse türü ne olursa
              olsun düzenlenebilir kalsın (ekstre düzeltmesi gelirse diye). */}
          {(k.tur === 'kart' || k.asgari > 0) && (
            <AsgariSatiri k={k} t={t} alan={alan} fxli={fxli} gonder={gonder} />
          )}
          {k.hareketler.length > 0 ? k.hareketler.map((h: Hareket) => (
            <HareketSatiri key={h.id} h={h} k={k} t={t} alan={alan} kisi={kisi}
              gonder={gonder} fxli={fxli} />
          )) : <p className={`text-[11px] ${t.muted} px-2`}>Henüz ödeme yok.</p>}

          <div className="flex items-center gap-1.5 flex-wrap pt-1">
            <select value={kid} onChange={e => setKid(Number(e.target.value))} className={alan} title="kim ödedi">
              {kisiler.map((p: Kisi) => <option key={p.id} value={p.id}>{p.emoji} {p.ad}</option>)}
              {kisiler.length === 0 && <option value={0}>— kişi yok —</option>}
            </select>
            <input type="date" value={tarih} onChange={e => setTarih(e.target.value)} className={alan} />
            <input value={tutar} onChange={e => setTutar(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') ode(); }}
              placeholder={k.kalan > 0 ? `kalan ${k.kalan}` : 'tutar'} inputMode="decimal"
              className={`${alan} w-28 tabular-nums`} />
            {fxli && (
              <span className="flex items-center gap-1">
                <input value={bid} onChange={e => setBid(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') ode(); }}
                  placeholder="bid" inputMode="decimal" title="bu ödemenin USDC/TRY alış kuru"
                  className={`${alan} w-20 tabular-nums`} />
                <span className={`text-[10px] ${t.muted}`}>
                  {(() => {
                    const b = parseFloat((bid || '').replace(',', '.')) || 0;
                    const v = parseFloat(tutar.replace(',', '.')) || k.kalan || 0;
                    const ef = b * (1 - (fx?.kom || 0) / 100);
                    return ef > 0 && v > 0 ? usdc(Math.round((v / ef) * 100) / 100) : 'kur gir';
                  })()}
                </span>
              </span>
            )}
            <button onClick={ode}
              className="px-2.5 py-1.5 rounded-lg text-[11px] border border-emerald-500/45 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20">
              {k.yon === 'gelir' ? 'tahsilat ekle' : 'ödeme ekle'}
            </button>
            {k.kalan > 0 && (
              <button onClick={tamami}
                className={`px-2.5 py-1.5 rounded-lg text-[11px] border ${t.sub} ${t.muted} hover:text-sky-400`}>
                kalanın tamamı
              </button>
            )}
            <button onClick={() => { if (window.confirm(`"${k.ad}" kalemi ve ödemeleri silinsin mi?`)) gonder('budget_delete_kalem', { id: k.id }); }}
              className={`ml-auto px-2 py-1.5 rounded-lg text-[11px] border ${t.sub} ${t.muted} hover:text-rose-400`}>
              <Trash2 size={11} className="inline -mt-0.5" /> kalemi sil
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── asgari ödeme satırı ──────────────────────────────────────────────────────
// Kaydetme save_kalem'in id'li yolundan geçiyor; o yol TÜM alanları yazdığı için
// ad/yön/tür/para/tutar/vade da geri gönderilmek ZORUNDA, yoksa varsayılanlarla
// ezilirler. Tutarı burada değiştirmiyoruz — ekstre tutarı kendi yerinde kalıyor.
function AsgariSatiri({ k, t, alan, fxli, gonder }: any) {
  const [v, setV] = useState('');
  const yaz = (deger: number) => {
    gonder('budget_save_kalem', {
      id: k.id, sablonId: k.sablonId, kisiId: k.kisiId, ad: k.ad, yon: k.yon,
      tur: k.tur, para: k.para, tutar: k.tutar, vade: k.vade, notu: k.notu,
      // TUZAK: bu yol TÜM alanları yazıyor — otoTahsil geri gönderilmezse tik silinir.
      asgari: deger, otoTahsil: !!k.otoTahsil,
    });
    setV('');
  };
  const kaydet = () => {
    const n = parseFloat((v || '').replace(',', '.'));
    if (!(n > 0)) return;
    yaz(n);
  };
  return (
    <div className={`flex items-center gap-1.5 flex-wrap text-[11px] px-2 py-1 rounded ${t.sub} border`}>
      <span className={t.muted}>asgari ödeme</span>
      {k.asgari > 0 && (
        <>
          <span className="tabular-nums font-medium">{para(k.asgari, k.para)}</span>
          {k.asgariOdendi
            ? <span className="text-emerald-400">ödendi</span>
            : <span className="text-amber-400 tabular-nums">
                {para(k.asgariKalan, k.para)} kaldı
                {fxli && k.usdAsgariKalan != null && (
                  <span className={t.muted}> ({usdc(k.usdAsgariKalan)})</span>
                )}
              </span>}
        </>
      )}
      <input value={v} onChange={e => setV(e.target.value)}
        onKeyDown={e => { if (e.key === 'Enter') kaydet(); }}
        placeholder={k.asgari > 0 ? 'değiştir' : 'asgari tutarı'} inputMode="decimal"
        className={`${alan} ml-auto w-28 tabular-nums !py-0.5`} />
      {!!v.trim() && (
        <button onClick={kaydet} className="text-sky-300 hover:text-sky-200" title="asgariyi kaydet">
          <Check size={12} />
        </button>
      )}
      {k.asgari > 0 && !v.trim() && (
        <button onClick={() => yaz(0)} className={`${t.muted} hover:text-rose-400`}
          title="asgariyi kaldır">
          <X size={12} />
        </button>
      )}
    </div>
  );
}

// ── tek bir ödeme satırı ─────────────────────────────────────────────────────
// Kur'u olmayan satır (kolon eklenmeden önce girilmiş ödemeler) sessizce
// "kur yok" diye kalmasın: yerinde kur yazılabiliyor. Güncelleme save_hareket'in
// id'li yolundan geçtiği için diğer alanları da geri göndermek ZORUNLU —
// göndermezsek tarih/tutar/kişi boşla ezilir.
function HareketSatiri({ h, k, t, alan, kisi, gonder, fxli }: any) {
  const [bid, setBid] = useState('');
  const hk = kisi(h.kisiId);
  const yaz = () => {
    const b = parseFloat((bid || '').replace(',', '.'));
    if (!(b > 0)) return;
    gonder('budget_save_hareket', {
      id: h.id, kalemId: k.id, tutar: h.tutar, tarih: h.tarih,
      kisiId: h.kisiId, notu: h.notu, kur: b,
    });
    setBid('');
  };
  return (
    <div className={`flex items-center gap-2 text-[11px] px-2 py-1 rounded ${t.sub} border`}>
      <span className="text-sm w-5 text-center" title={hk?.ad || ''}>{hk?.emoji || '—'}</span>
      <span className={`font-mono ${t.muted}`}>{h.tarih || '—'}</span>
      <span className="tabular-nums text-emerald-400">{para(h.tutar, k.para)}</span>
      {fxli && (h.usd != null ? (
        <span className={`tabular-nums ${t.muted}`}
          title={`bid ${h.kurHam} · komisyon düşülmüş efektif ${h.kur}`}>
          {usdc(h.usd)}
        </span>
      ) : (
        <>
          <span className="text-[10px] text-amber-400" title="bu ödeme kur kaydedilmeden girilmiş">
            kur yok
          </span>
          <input value={bid} onChange={e => setBid(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') yaz(); }}
            placeholder="o günün bid'i" inputMode="decimal"
            className={`${alan} w-28 tabular-nums !py-0.5`} />
          {!!bid.trim() && (
            <button onClick={yaz} className="text-sky-300 hover:text-sky-200" title="kuru yaz">
              <Check size={12} />
            </button>
          )}
        </>
      ))}
      {h.notu && <span className={`truncate ${t.muted}`}>{h.notu}</span>}
      <button onClick={() => gonder('budget_delete_hareket', { id: h.id })}
        className={`ml-auto ${t.muted} hover:text-rose-400`} title="ödemeyi sil">
        <Trash2 size={11} />
      </button>
    </div>
  );
}

// ── tek seferlik kalem ───────────────────────────────────────────────────────
function TekSeferlik({ d, t, alan, ay, kapat, gonder }: any) {
  const [f, setF] = useState<any>({
    ad: '', yon: 'gider', tur: 'diger', para: 'EUR', tutar: '', gun: String(new Date().getDate()),
    kisiId: String(d?.kisiler[0]?.id || 0), otoTahsil: false,
  });
  const set = (k: string, v: string) => setF((p: any) => ({ ...p, [k]: v }));
  const ok = f.ad.trim() && parseFloat((f.tutar || '').replace(',', '.')) > 0;
  return (
    <div className={`rounded-xl border ${t.card} p-3 mb-4`}>
      <div className="flex items-center gap-2 mb-2">
        <span className="text-xs font-semibold">Tek seferlik kalem — {ayEtiket(ay)}</span>
        <button onClick={kapat} className={`ml-auto ${t.muted} hover:text-rose-400`}><X size={14} /></button>
      </div>
      <div className="flex items-end gap-1.5 flex-wrap">
        <input value={f.ad} onChange={e => set('ad', e.target.value)} placeholder="ad (ör. uçak bileti)"
          className={`${alan} flex-1 min-w-[10rem]`} />
        <select value={f.yon} onChange={e => set('yon', e.target.value)} className={alan}>
          <option value="gider">gider</option><option value="gelir">gelir</option>
        </select>
        <select value={f.tur} onChange={e => set('tur', e.target.value)} className={alan}>
          {TURLER.map(x => <option key={x} value={x}>{TUR_AD[x]}</option>)}
        </select>
        <select value={f.kisiId} onChange={e => set('kisiId', e.target.value)} className={alan}>
          {d?.kisiler.map((p: Kisi) => <option key={p.id} value={p.id}>{p.emoji} {p.ad}</option>)}
          {!d?.kisiler.length && <option value="0">— kişi yok —</option>}
        </select>
        <input value={f.tutar} onChange={e => set('tutar', e.target.value)} placeholder="tutar"
          inputMode="decimal" className={`${alan} w-24 tabular-nums`} />
        <select value={f.para} onChange={e => set('para', e.target.value)} className={alan}>
          {PARALAR.map(x => <option key={x} value={x}>{x}</option>)}
        </select>
        <input value={f.gun} onChange={e => set('gun', e.target.value)} placeholder="gün"
          inputMode="numeric" className={`${alan} w-16 tabular-nums`} title="ayın kaçı" />
        {f.yon === 'gelir' && (
            <label className={`flex items-center gap-1.5 text-[11px] ${t.muted} cursor-pointer mr-1`}
              title="Banka kendiliğinden yatırıyor — vadesi gelince tahsil edilmiş sayılır">
              <input type="checkbox" checked={!!f.otoTahsil}
                onChange={e => setF((p: any) => ({ ...p, otoTahsil: e.target.checked }))}
                className="accent-emerald-500" />
              otomatik yatar
            </label>
          )}
          <button disabled={!ok}
          onClick={() => { gonder('budget_save_kalem', { ...f, tutar: parseFloat(f.tutar.replace(',', '.')), gun: parseInt(f.gun, 10) || 1, kisiId: parseInt(f.kisiId, 10) || 0 }); kapat(); }}
          className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-600/25 border border-sky-500/50 text-sky-300 hover:bg-sky-600/40 disabled:opacity-40">
          Ekle
        </button>
      </div>
    </div>
  );
}

// ── kişiler + şablonlar ──────────────────────────────────────────────────────
function Ayarlar({ d, t, alan, gonder }: any) {
  const bos = {
    id: 0, ad: '', yon: 'gider', tur: 'kira', para: 'EUR', gun: '1', tutar: '',
    otomatik: true, otoTahsil: false, basAy: '', sonAy: '', kisiId: String(d?.kisiler[0]?.id || 0),
  };
  const [s, setS] = useState<any>(bos);
  const [kAd, setKAd] = useState(''); const [kEmoji, setKEmoji] = useState('🙂');
  const set = (k: string, v: any) => setS((p: any) => ({ ...p, [k]: v }));
  const sOk = s.ad.trim() && (!s.otomatik || parseFloat((s.tutar || '').replace(',', '.')) > 0);

  return (
    <div className={`rounded-xl border ${t.card} p-3 mb-4 space-y-4`}>
      {/* kişiler */}
      <div>
        <div className={`text-[11px] ${t.muted} mb-1.5`}>Kişiler — emoji ödeme satırlarında görünür</div>
        <div className="flex items-center gap-1.5 flex-wrap mb-2">
          {d?.kisiler.map((p: Kisi) => (
            <span key={p.id} className={`flex items-center gap-1 text-xs px-2 py-1 rounded-lg border ${t.sub}`}>
              <span className="text-sm">{p.emoji}</span>{p.ad}
              <button onClick={() => { if (window.confirm(`${p.ad} silinsin mi? Kayıtlar kalır, sahipsiz olur.`)) gonder('budget_delete_kisi', { id: p.id }); }}
                className={`${t.muted} hover:text-rose-400 ml-0.5`}><X size={11} /></button>
            </span>
          ))}
          {!d?.kisiler.length && <span className={`text-xs ${t.muted}`}>Henüz kişi yok.</span>}
        </div>
        <div className="flex items-center gap-1.5">
          <input value={kEmoji} onChange={e => setKEmoji(e.target.value)} className={`${alan} w-14 text-center text-base`} />
          <input value={kAd} onChange={e => setKAd(e.target.value)} placeholder="kişi adı"
            onKeyDown={e => { if (e.key === 'Enter' && kAd.trim()) { gonder('budget_save_kisi', { ad: kAd, emoji: kEmoji }); setKAd(''); } }}
            className={`${alan} w-40`} />
          <button disabled={!kAd.trim()}
            onClick={() => { gonder('budget_save_kisi', { ad: kAd, emoji: kEmoji }); setKAd(''); }}
            className="px-2.5 py-1.5 rounded-lg text-[11px] border border-sky-500/45 bg-sky-500/10 text-sky-300 disabled:opacity-40">
            <Plus size={11} className="inline -mt-0.5" /> kişi
          </button>
        </div>
      </div>

      {/* şablonlar */}
      <div className={`pt-3 border-t ${t.sub}`}>
        <div className={`text-[11px] ${t.muted} mb-1.5`}>
          Şablonlar — <b>otomatik</b> olanlar (kira, kredi, maaş) her ay kendiliğinden düşer;
          kredi kartında tutarı her ay sen yazarsın. Kredide <b>son taksit ayı</b> yazarsan o aydan sonra gelmez.
        </div>
        <div className="space-y-1 mb-2">
          {d?.sablonlar.map((x: Sablon) => {
            const sahip = d.kisiler.find((p: Kisi) => p.id === x.kisi_id);
            return (
              <div key={x.id} className={`flex items-center gap-2 text-[11px] px-2 py-1.5 rounded-lg border ${t.sub} ${x.aktif ? '' : 'opacity-50'}`}>
                <span className="text-sm w-5 text-center">{sahip?.emoji || '—'}</span>
                <span className={`tabular-nums w-6 ${t.muted}`}>{String(x.gun).padStart(2, '0')}</span>
                <span className="font-medium truncate">{x.ad}</span>
                <span className={`px-1.5 rounded border ${t.sub} ${t.muted}`}>{TUR_AD[x.tur] || x.tur}</span>
                <span className={x.yon === 'gelir' ? 'text-emerald-400' : 'text-rose-400'}>{x.yon}</span>
                <span className="tabular-nums ml-auto">
                  {x.otomatik ? para(x.tutar, x.para) : <span className={t.muted}>elle · {x.para}</span>}
                </span>
                {x.son_ay && <span className="text-amber-400 shrink-0">son {x.son_ay}</span>}
                <button onClick={() => setS({
                    id: x.id, ad: x.ad, yon: x.yon, tur: x.tur, para: x.para, gun: String(x.gun),
                    tutar: x.tutar ? String(x.tutar) : '', otomatik: !!x.otomatik,
                    otoTahsil: !!x.oto_tahsil,
                    basAy: x.bas_ay || '', sonAy: x.son_ay || '', kisiId: String(x.kisi_id),
                  })}
                  className={`${t.muted} hover:text-sky-400 shrink-0`}><Pencil size={11} /></button>
                <button onClick={() => { if (window.confirm(`"${x.ad}" şablonu silinsin mi? Geçmiş aylardaki kalemler KALIR.`)) gonder('budget_delete_sablon', { id: x.id }); }}
                  className={`${t.muted} hover:text-rose-400 shrink-0`}><Trash2 size={11} /></button>
              </div>
            );
          })}
          {!d?.sablonlar.length && <p className={`text-xs ${t.muted}`}>Henüz şablon yok.</p>}
        </div>

        <div className="flex items-end gap-1.5 flex-wrap">
          <input value={s.ad} onChange={e => set('ad', e.target.value)} placeholder="ad (Kira / Konut kredisi / Maaş)"
            className={`${alan} flex-1 min-w-[11rem]`} />
          <select value={s.kisiId} onChange={e => set('kisiId', e.target.value)} className={alan}>
            {d?.kisiler.map((p: Kisi) => <option key={p.id} value={p.id}>{p.emoji} {p.ad}</option>)}
            {!d?.kisiler.length && <option value="0">— kişi yok —</option>}
          </select>
          <select value={s.yon} onChange={e => set('yon', e.target.value)} className={alan}>
            <option value="gider">gider</option><option value="gelir">gelir</option>
          </select>
          <select value={s.tur} onChange={e => {
              const v = e.target.value;
              // Kart = tutarı her ay elle girilen tür; seçilince otomatik kapanır.
              setS((p: any) => ({ ...p, tur: v, otomatik: v === 'kart' ? false : p.otomatik }));
            }} className={alan}>
            {TURLER.map(x => <option key={x} value={x}>{TUR_AD[x]}</option>)}
          </select>
          <input value={s.gun} onChange={e => set('gun', e.target.value)} placeholder="gün"
            inputMode="numeric" className={`${alan} w-16 tabular-nums`} title="ayın kaçı" />
          <select value={s.para} onChange={e => set('para', e.target.value)} className={alan}>
            {PARALAR.map(x => <option key={x} value={x}>{x}</option>)}
          </select>
          <label className={`flex items-center gap-1 text-[11px] px-2 py-1.5 rounded-lg border ${t.sub} cursor-pointer`}>
            <input type="checkbox" className="accent-sky-500" checked={s.otomatik}
              onChange={e => set('otomatik', e.target.checked)} />
            otomatik düşsün
          </label>
          {s.otomatik && (
            <input value={s.tutar} onChange={e => set('tutar', e.target.value)} placeholder="tutar"
              inputMode="decimal" className={`${alan} w-24 tabular-nums`} />
          )}
          <input value={s.sonAy} onChange={e => set('sonAy', e.target.value)} placeholder="son taksit (2027-04)"
            className={`${alan} w-36`} title="kredide son taksit ayı — bu aydan sonra düşmez" />
          {/* YALNIZ GELİRDE. Gideri "kendiliğinden ödendi" saymak, parayı
              kaydetmeden harcanmış göstermek olurdu. */}
          {s.yon === 'gelir' && (
            <label className={`flex items-center gap-1.5 text-[11px] ${t.muted} cursor-pointer`}
              title="Banka kendiliğinden yatırıyor — vadesi gelince tahsil edilmiş sayılır, elle satır girmek gerekmez">
              <input type="checkbox" checked={!!s.otoTahsil}
                onChange={e => set('otoTahsil', e.target.checked)} className="accent-emerald-500" />
              otomatik yatar
            </label>
          )}
          <button disabled={!sOk}
            onClick={() => {
              gonder('budget_save_sablon', {
                ...s, gun: parseInt(s.gun, 10) || 1, kisiId: parseInt(s.kisiId, 10) || 0,
                tutar: s.otomatik ? parseFloat((s.tutar || '0').replace(',', '.')) : 0,
              });
              setS(bos);
            }}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-sky-600/25 border border-sky-500/50 text-sky-300 hover:bg-sky-600/40 disabled:opacity-40">
            {s.id ? 'Güncelle' : 'Şablon ekle'}
          </button>
          {!!s.id && (
            <button onClick={() => setS(bos)} className={`px-2.5 py-1.5 rounded-lg text-[11px] border ${t.sub} ${t.muted}`}>
              vazgeç
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
